import type { Release } from '@src/dto/Release';
import { Button } from '../Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';

interface DeleteReleaseDialogProps {
  releasePendingDelete: Release | null;
  deleteReason: string;
  isDeletingRelease: boolean;
  onClose: () => void;
  onDelete: () => void | Promise<void>;
  onDeleteReasonChange: (reason: string) => void;
}

export function DeleteReleaseDialog({
  releasePendingDelete,
  deleteReason,
  isDeletingRelease,
  onClose,
  onDelete,
  onDeleteReasonChange,
}: DeleteReleaseDialogProps) {
  return (
    <Dialog
      open={!!releasePendingDelete}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-md rounded-xl border-seeker-800 bg-seeker-900 p-6">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-white">
            Delete release?
          </DialogTitle>
          <DialogDescription className="text-sm text-seeker-400">
            Only superadmins can delete releases. This performs a soft delete,
            removes the release from portal views, and leaves an audit trail.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3">
            <div className="truncate text-sm font-medium tabular-nums text-red-100">
              {releasePendingDelete?.versionName || 'Unknown release'}
              {releasePendingDelete?.versionCode
                ? ` (${releasePendingDelete.versionCode})`
                : ''}
            </div>
            <div
              className="mt-1 truncate text-xs text-red-200/80"
              title={releasePendingDelete?.releaseFileName}
            >
              {releasePendingDelete?.releaseFileName}
            </div>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="release-delete-reason"
              className="text-sm font-bold text-white"
            >
              Reason for deletion (optional)
            </label>
            <Textarea
              id="release-delete-reason"
              value={deleteReason}
              onChange={(event) => onDeleteReasonChange(event.target.value)}
              placeholder="Add context for the audit trail"
              className="rounded-lg border-seeker-700 bg-seeker-950 text-white placeholder:text-seeker-500"
              rows={4}
              maxLength={500}
              disabled={isDeletingRelease}
            />
          </div>
        </DialogBody>

        <DialogFooter className="gap-3 sm:space-x-0">
          <Button
            variant="outlineMuted"
            className="min-w-[120px]"
            onClick={onClose}
            disabled={isDeletingRelease}
          >
            Cancel
          </Button>
          <Button
            variant="dangerOutline"
            className="min-w-[140px]"
            onClick={onDelete}
            disabled={isDeletingRelease}
            submitting={isDeletingRelease}
          >
            Delete release
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
